import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SearchableSiteSelect from "./SearchableSiteSelect";

function formatDate(val) {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function yn(value) {
  return value ? "Y" : "N";
}

function parseISODate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function toISODate(value) {
  if (!value) return "";
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function handleFieldFocus(e) {
  e.target.style.borderColor = "#8b1634";
  e.target.style.boxShadow = "0 0 0 0.15rem rgba(139, 22, 52, 0.15)";
}

function handleFieldBlur(e) {
  e.target.style.borderColor = "#e2e8f0";
  e.target.style.boxShadow = "none";
}

function getStatusBadgeStyle(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "retired") {
    return {
      background: "#fef2f2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }
  if (normalized === "assigned" || normalized === "active") {
    return {
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
    };
  }
  if (normalized === "unassigned") {
    return {
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }
  return {
    background: "#f8fafc",
    color: "#475569",
    border: "1px solid #e2e8f0",
  };
}

function normalizePhotoUrls(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

function photoSrcForDisplay(value) {
  if (value == null || typeof value !== "string") return "";
  const s = value.trim();
  if (!s) return "";
  if (s.startsWith("data:")) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith("//") || s.startsWith("blob:")) return s;
  // Server-hosted file (e.g. "/uploads/assets/x.jpg") — prefix the backend host.
  if (s.startsWith("/uploads/")) {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
    return `${baseUrl}${s}`;
  }
  const compact = s.replace(/\s/g, "");
  if (compact.length < 80 || !/^[A-Za-z0-9+/]+=*$/.test(compact)) return s;
  if (compact.startsWith("iVBORw0KGgo") || compact.startsWith("iVBOR")) return `data:image/png;base64,${compact}`;
  if (compact.startsWith("R0lGOD")) return `data:image/gif;base64,${compact}`;
  if (compact.startsWith("UklGR")) return `data:image/webp;base64,${compact}`;
  if (compact.startsWith("/9j")) return `data:image/jpeg;base64,${compact}`;
  return `data:image/jpeg;base64,${compact}`;
}

export default function AssetsListPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const openedEditFromQueryRef = useRef(false);
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [assetStatusFilter, setAssetStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [deleteAssetCode, setDeleteAssetCode] = useState("");
  const [photoPreviewSrc, setPhotoPreviewSrc] = useState("");

  useEffect(() => {
    fetchAssets(true);
    fetchSites();
  }, []);

  useEffect(() => {
    if (!showEditModal) setPhotoPreviewSrc("");
  }, [showEditModal]);

  const fetchAssets = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/asset`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setItems([]);
        setError(data?.message || "Failed to load assets");
        return;
      }
      setItems(Array.isArray(data.data) ? data.data : []);
    } catch {
      setItems([]);
      setError("Failed to load assets");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setSites([]);
        return;
      }
      setSites(Array.isArray(data.data) ? data.data : []);
    } catch {
      setSites([]);
    }
  };

  const handleEdit = (row) => {
    setEditAsset({
      labelCode: row.labelCode || "",
      dateOfPurchase: row.dateOfPurchase ? String(row.dateOfPurchase).slice(0, 10) : "",
      serialNumber: row.serialNumber || "",
      modelNumber: row.modelNumber || "",
      photoUrl: normalizePhotoUrls(row.photoUrl),
      location: row.location || "",
      siteId: row.siteId || "",
      siteName: row.siteName || "",
      address: row.address || "",
      postCode: row.postCode || "",
      clientName: row.clientName || "",
      lastMaintenanceDate: row.lastMaintenanceDate ? String(row.lastMaintenanceDate).slice(0, 10) : "",
      cleaned: Boolean(row.cleaned),
      repairMaintenanceRequired: Boolean(row.repairMaintenanceRequired),
      replacementRequired: Boolean(row.replacementRequired),
      notes: row.notes || "",
      deviceCondition: row.deviceCondition || "Good",
      deviceName: row.deviceName || "Asset",
    });
    setShowEditModal(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const label = (params.get("edit") || "").trim();
    if (!label) return;
    if (openedEditFromQueryRef.current) return;
    if (loading) return;
    const match = items.find((x) => String(x.labelCode || "") === label);
    if (!match) return;
    openedEditFromQueryRef.current = true;
    handleEdit(match);
    navigate(location.pathname, { replace: true });
  }, [items, loading, location.pathname, location.search, navigate]);

  const handleEditSave = async () => {
    if (!editAsset?.labelCode) return;
    if (!editAsset?.siteId) {
      toast.error("Please select a site");
      return;
    }
    const saveButton = document.querySelector(".modal-footer button[type='button']:last-child");
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/assets/${encodeURIComponent(editAsset.labelCode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editAsset),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to update asset");
        return;
      }
      toast.success("Asset updated successfully!");
      setShowEditModal(false);
      setEditAsset(null);
      await fetchAssets(false);
    } catch {
      toast.error("Failed to update asset");
    } finally {
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.innerHTML = "Save";
      }
    }
  };

  const handleDelete = (labelCode) => {
    setDeleteAssetCode(labelCode || "");
    setShowDeleteModal(true);
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
            reader.readAsDataURL(file);
          })
      )
    ).then((results) => {
      const valid = results.filter((x) => Boolean(x));
      if (!valid.length) return;
      setEditAsset((prev) =>
        prev
          ? {
              ...prev,
              photoUrl: [...normalizePhotoUrls(prev.photoUrl), ...valid],
            }
          : prev
      );
    });
    event.target.value = "";
  };

  const removePhotoAtIndex = (index) => {
    setEditAsset((prev) => {
      if (!prev) return prev;
      const next = normalizePhotoUrls(prev.photoUrl).filter((_, idx) => idx !== index);
      return { ...prev, photoUrl: next };
    });
  };

  const confirmDelete = async () => {
    if (!deleteAssetCode) return;
    const deleteButton = document.querySelector(".modal-footer button.btn-danger");
    if (deleteButton) {
      deleteButton.disabled = true;
      deleteButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Deleting...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/asset/${encodeURIComponent(deleteAssetCode)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to delete asset");
        return;
      }
      toast.success("Asset deleted successfully!");
      setShowDeleteModal(false);
      setDeleteAssetCode("");
      await fetchAssets(false);
    } catch {
      toast.error("Failed to delete asset");
    } finally {
      if (deleteButton) {
        deleteButton.disabled = false;
        deleteButton.innerHTML = "Delete";
      }
    }
  };

  const filtered = items.filter((row) => {
    const normalizedAssetStatus = String(row.assetStatus || "active").toLowerCase();
    if (assetStatusFilter !== "all" && normalizedAssetStatus !== assetStatusFilter) {
      return false;
    }
    if (clientFilter && String(row.clientName || "").trim().toLowerCase() !== clientFilter.toLowerCase()) {
      return false;
    }
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const text = [
      row.labelCode,
      row.status,
      row.assetStatus,
      row.deviceName,
      row.location,
      row.serialNumber,
      row.modelNumber,
      row.manufacturer,
      row.siteName,
      row.address,
      row.postCode,
      row.clientName,
      row.deviceCondition,
      row.notes,
    ]
      .map((v) => String(v || "").toLowerCase())
      .join(" ");
    return text.includes(q);
  });
  const clientOptions = [...new Set(items.map((x) => String(x.clientName || "").trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 700, color: "#1a202c", margin: 0 }}>Assets & QR Labels</h2>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            background: "linear-gradient(180deg, #fff7f9 0%, #ffffff 100%)",
            border: "1px solid #f1d6df",
            borderRadius: "12px",
            padding: "8px",
          }}
        >
          <div style={{ position: "relative", minWidth: "190px" }}>
            <i
              className="fa fa-filter"
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#8b1634",
                fontSize: "12px",
                pointerEvents: "none",
              }}
            />
            <select
              value={assetStatusFilter}
              onChange={(e) => setAssetStatusFilter(e.target.value)}
              style={{
                width: "100%",
                border: "1px solid #e8c4cf",
                borderRadius: "10px",
                padding: "8px 10px 8px 30px",
                color: "#8b1634",
                background: "#ffffff",
                fontWeight: 700,
                outline: "none",
              }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div style={{ position: "relative", minWidth: "190px" }}>
            <i
              className="fa fa-building"
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#8b1634",
                fontSize: "12px",
                pointerEvents: "none",
              }}
            />
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              style={{
                width: "100%",
                border: "1px solid #e8c4cf",
                borderRadius: "10px",
                padding: "8px 10px 8px 30px",
                color: "#8b1634",
                background: "#ffffff",
                fontWeight: 700,
                outline: "none",
              }}
            >
              <option value="">All clients</option>
              {clientOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div style={{ position: "relative", width: "270px", maxWidth: "100%" }}>
            <i
              className="fa fa-search"
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#8b1634",
                fontSize: "12px",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search labels/assets"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                border: "1px solid #e8c4cf",
                borderRadius: "10px",
                padding: "8px 12px 8px 30px",
                outline: "none",
                color: "#1f2937",
                fontWeight: 600,
                background: "#ffffff",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#8b1634";
                e.target.style.boxShadow = "0 0 0 0.15rem rgba(139, 22, 52, 0.12)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e8c4cf";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
          {(assetStatusFilter !== "all" || clientFilter || search.trim()) ? (
            <button
              type="button"
              onClick={() => {
                setAssetStatusFilter("all");
                setClientFilter("");
                setSearch("");
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "10px",
                border: "none",
                background: "#e53e3e",
                color: "#fff",
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          ) : null}
        </div>
      </div>

      {error ? <div style={{ padding: "8px 0 16px", color: "#e53e3e" }}>{error}</div> : null}
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "12px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1820px" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={th}>Asset Number (labelCode)</th>
              <th style={th}>Asset Name</th>
              <th style={th}>Client Name</th>
              <th style={th}>Date of Purchase</th>
              <th style={th}>Serial Number</th>
              <th style={th}>Model Number</th>
              <th style={th}>Location</th>
              <th style={th}>Site Name</th>
              <th style={th}>Address</th>
              <th style={th}>Post Code</th>
              <th style={th}>Last Maintenance Date</th>
              <th style={th}>Condition</th>
              <th style={th}>Notes</th>
              <th style={th}>Asset Status</th>
              <th style={th}>QR Status</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: "#718096", padding: "28px 12px" }} colSpan={16}>
                  Loading data...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: "#718096" }} colSpan={16}>
                  No records found.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row._id}>
                  <td style={td}>{row.labelCode || "-"}</td>
                  <td style={td}>{row.deviceName || "-"}</td>
                  <td style={td}>{row.clientName || "-"}</td>
                  <td style={td}>{formatDate(row.dateOfPurchase)}</td>
                  <td style={td}>{row.serialNumber || "-"}</td>
                  <td style={td}>{row.modelNumber || "-"}</td>
                  <td style={td}>{row.location || "-"}</td>
                  <td style={td}>{row.siteName || "-"}</td>
                  <td style={td}>{row.address || "-"}</td>
                  <td style={td}>{row.postCode || "-"}</td>
                  <td style={td}>{formatDate(row.lastMaintenanceDate)}</td>
                  <td style={td}>{row.deviceCondition || "-"}</td>
                  <td style={td}>{row.notes || "-"}</td>
                  <td style={td}>
                    <span style={{ ...statusBadgeBase, ...getStatusBadgeStyle(row.assetStatus), textTransform: "capitalize" }}>
                      {row.assetStatus || "active"}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{ ...statusBadgeBase, ...getStatusBadgeStyle(row.status), textTransform: "capitalize" }}>
                      {row.status || "-"}
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <button
                        onClick={() => handleEdit(row)}
                        title="Edit"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "6px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0fdf4")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: "16px" }} />
                      </button>
                      <button
                        onClick={() => handleDelete(row.labelCode)}
                        title="Delete"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "6px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fef2f2")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: "16px" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showEditModal && editAsset ? (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "16px", overflow: "hidden" }}>
              <div className="modal-header border-0" style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", padding: "14px 18px" }}>
                <h5 className="modal-title" style={{ color: "#fff", fontWeight: 700, fontSize: "18px" }}>Edit Asset</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ background: "#f8fafc", padding: "18px" }}>
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 14px 6px", marginBottom: "12px" }}>
                  <div className="row mb-2">
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Asset Number</label>
                      <input className="form-control asset-edit-control" value={editAsset.labelCode} disabled />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Client Name</label>
                      <input className="form-control asset-edit-control" value={editAsset.clientName || ""} onChange={(e) => setEditAsset({ ...editAsset, clientName: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Date of Purchase</label>
                      <DatePicker
                        selected={parseISODate(editAsset.dateOfPurchase)}
                        onChange={(date) => setEditAsset({ ...editAsset, dateOfPurchase: toISODate(date) })}
                        dateFormat="dd-MM-yyyy"
                        className="form-control asset-edit-control"
                        calendarClassName="asset-theme-calendar"
                        popperClassName="asset-theme-calendar-popper"
                        placeholderText="DD-MM-YYYY"
                      />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Serial Number</label>
                      <input className="form-control asset-edit-control" value={editAsset.serialNumber} onChange={(e) => setEditAsset({ ...editAsset, serialNumber: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 14px 6px", marginBottom: "12px" }}>
                  <div className="row mb-2">
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Model Number</label>
                      <input className="form-control asset-edit-control" value={editAsset.modelNumber} onChange={(e) => setEditAsset({ ...editAsset, modelNumber: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Location</label>
                      <input className="form-control asset-edit-control" value={editAsset.location} onChange={(e) => setEditAsset({ ...editAsset, location: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Site</label>
                      <SearchableSiteSelect
                        value={editAsset.siteId || ""}
                        onChange={(v) => {
                          const selectedSite = sites.find((s) => String(s._id) === String(v));
                          setEditAsset({
                            ...editAsset,
                            siteId: v,
                            siteName: selectedSite?.siteName || "",
                            address: selectedSite?.siteLocation || "",
                          });
                        }}
                        options={sites.map((site) => ({ value: site._id, label: site.siteName }))}
                        placeholder="Select site"
                      />
                    </div>
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 14px 6px", marginBottom: "12px" }}>
                  <div className="row mb-2">
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Address</label>
                      <input className="form-control asset-edit-control" value={editAsset.address} onChange={(e) => setEditAsset({ ...editAsset, address: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Post Code</label>
                      <input className="form-control asset-edit-control" value={editAsset.postCode} onChange={(e) => setEditAsset({ ...editAsset, postCode: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Last Maintenance Date</label>
                      <DatePicker
                        selected={parseISODate(editAsset.lastMaintenanceDate)}
                        onChange={(date) => setEditAsset({ ...editAsset, lastMaintenanceDate: toISODate(date) })}
                        dateFormat="dd-MM-yyyy"
                        className="form-control asset-edit-control"
                        calendarClassName="asset-theme-calendar"
                        popperClassName="asset-theme-calendar-popper"
                        placeholderText="DD-MM-YYYY"
                      />
                    </div>
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "14px 14px 6px", marginBottom: "12px" }}>
                  <div className="row mb-2">
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Cleaned</label>
                      <select className="form-control asset-edit-control" value={editAsset.cleaned ? "Y" : "N"} onChange={(e) => setEditAsset({ ...editAsset, cleaned: e.target.value === "Y" })} onFocus={handleFieldFocus} onBlur={handleFieldBlur}><option value="Y">Y</option><option value="N">N</option></select>
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Repair/Maintenance Required</label>
                      <select className="form-control asset-edit-control" value={editAsset.repairMaintenanceRequired ? "Y" : "N"} onChange={(e) => setEditAsset({ ...editAsset, repairMaintenanceRequired: e.target.value === "Y" })} onFocus={handleFieldFocus} onBlur={handleFieldBlur}><option value="Y">Y</option><option value="N">N</option></select>
                    </div>
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Replacement Required</label>
                      <select className="form-control asset-edit-control" value={editAsset.replacementRequired ? "Y" : "N"} onChange={(e) => setEditAsset({ ...editAsset, replacementRequired: e.target.value === "Y" })} onFocus={handleFieldFocus} onBlur={handleFieldBlur}><option value="Y">Y</option><option value="N">N</option></select>
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-4 mb-2">
                      <label style={fieldLabel}>Condition</label>
                      <select className="form-control asset-edit-control" value={editAsset.deviceCondition} onChange={(e) => setEditAsset({ ...editAsset, deviceCondition: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur}>
                        <option value="Good">Good</option>
                        <option value="Fault">Fault</option>
                        <option value="Maintenance required">Maintenance required</option>
                      </select>
                    </div>
                    <div className="col-8 mb-2">
                      <label style={fieldLabel}>Notes</label>
                      <input className="form-control asset-edit-control" value={editAsset.notes} onChange={(e) => setEditAsset({ ...editAsset, notes: e.target.value })} onFocus={handleFieldFocus} onBlur={handleFieldBlur} />
                    </div>
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "16px", marginTop: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827", letterSpacing: "0.01em" }}>Asset Photos</div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>Upload, preview and manage gallery images</div>
                    </div>
                    <span style={{ background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", borderRadius: "999px", padding: "4px 10px", fontSize: "12px", fontWeight: 700 }}>
                      {normalizePhotoUrls(editAsset.photoUrl).length} photo{normalizePhotoUrls(editAsset.photoUrl).length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", padding: "12px", marginBottom: "12px" }}>
                    <label style={{ ...fieldLabel, marginBottom: "8px", display: "block" }}>Upload Images</label>
                    <input type="file" accept="image/*" multiple className="form-control asset-edit-control" onChange={handlePhotoUpload} />
                  </div>
                  <div style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", minHeight: "250px", background: "#f8fafc", padding: "14px" }}>
                    {normalizePhotoUrls(editAsset.photoUrl).length ? (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "14px" }}>
                        {normalizePhotoUrls(editAsset.photoUrl).map((img, idx) => (
                          <div key={`${idx}-${img.slice(0, 20)}`} style={{ position: "relative", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                            <img
                              src={photoSrcForDisplay(img)}
                              alt={`Asset ${idx + 1}`}
                              role="presentation"
                              onClick={() => setPhotoPreviewSrc(img)}
                              style={{
                                width: "100%",
                                height: "138px",
                                objectFit: "cover",
                                cursor: "pointer",
                                display: "block",
                              }}
                              title="Click to preview"
                            />
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderTop: "1px solid #f1f5f9" }}>
                              <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>Image {idx + 1}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removePhotoAtIndex(idx);
                                }}
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  borderRadius: "999px",
                                  border: "1px solid #fecaca",
                                  background: "#fff1f2",
                                  color: "#be123c",
                                  fontSize: "13px",
                                  lineHeight: 1,
                                  cursor: "pointer",
                                  fontWeight: 700,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                title="Remove photo"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ minHeight: "220px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8", gap: "8px" }}>
                        <i className="fa fa-image" style={{ fontSize: "26px", opacity: 0.8 }} />
                        <span style={{ fontSize: "14px", fontWeight: 600 }}>No photos uploaded yet</span>
                        <span style={{ fontSize: "12px" }}>Use "Upload Images" to add one or more files</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0" style={{ background: "#fff", padding: "12px 18px", display: "flex", justifyContent: "space-between" }}>
                <button type="button" className="btn btn-light border" style={{ color: "#4a5568", fontWeight: 600, minWidth: "96px" }} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: "linear-gradient(135deg, #8b1634, #b42352)", color: "#fff", fontWeight: 700, border: "none", minWidth: "110px" }} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {photoPreviewSrc
        ? createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100vw",
                minHeight: "100vh",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.55)",
                zIndex: 10000,
                padding: "16px",
                boxSizing: "border-box",
              }}
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
              aria-labelledby="asset-photo-preview-title"
              onClick={() => setPhotoPreviewSrc("")}
            >
              <div
                style={{ width: "min(920px, 100%)", maxWidth: "100%", flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ borderRadius: "14px", overflow: "hidden", border: "none", boxShadow: "0 25px 60px rgba(0,0,0,0.35)", background: "#fff" }}>
                  <div
                    style={{
                      borderBottom: "1px solid #e2e8f0",
                      background: "#ffffff",
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                    <h5 id="asset-photo-preview-title" style={{ margin: 0, fontWeight: 700, color: "#111827", fontSize: "16px" }}>
                      Photo preview
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setPhotoPreviewSrc("")} aria-label="Close" />
                  </div>
                  <div
                    style={{
                      background: "#0f172a",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "320px",
                      maxHeight: "70vh",
                      overflow: "auto",
                      padding: "12px",
                    }}
                  >
                    <img
                      src={photoSrcForDisplay(photoPreviewSrc)}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "62vh",
                        objectFit: "contain",
                        borderRadius: "10px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                      }}
                    />
                  </div>
                  <div style={{ borderTop: "1px solid #e2e8f0", background: "#fff", padding: "10px 16px", display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="btn"
                      style={{
                        border: "1px solid #8b1634",
                        color: "#8b1634",
                        background: "transparent",
                        fontWeight: 600,
                        borderRadius: "8px",
                        padding: "7px 16px",
                      }}
                      onClick={() => setPhotoPreviewSrc("")}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {showDeleteModal ? (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "400px", width: "calc(100% - 2rem)" }}>
            <div className="modal-content border-0 shadow" style={{ borderRadius: "14px", overflow: "hidden" }}>
              <div className="modal-header border-0 py-3" style={{ background: "linear-gradient(135deg, #8b1634 0%, #b42352 100%)", color: "#fff" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: "17px" }}>Delete Asset</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body px-4 pt-3 pb-2">
                <p className="mb-0" style={{ color: "#4a5568" }}>Are you sure you want to delete this asset?</p>
              </div>
              <div className="modal-footer border-0 pt-0 pb-3 px-4 gap-2" style={{ background: "#fafafa" }}>
                <button className="btn btn-light border" style={{ color: "#4a5568", fontWeight: 600 }} onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger fw-semibold" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const th = {
  textAlign: "left",
  fontSize: "13px",
  color: "#8b1634",
  padding: "12px",
  borderBottom: "1px solid #e2e8f0",
};

const td = {
  textAlign: "left",
  fontSize: "13px",
  color: "#2d3748",
  padding: "12px",
  borderBottom: "1px solid #f1f5f9",
};

const fieldLabel = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#64748b",
  letterSpacing: "0.02em",
  marginBottom: "6px",
  display: "inline-block",
};

const statusBadgeBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "86px",
  padding: "4px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: 1.1,
};

const style = document.createElement("style");
style.innerHTML = `
.asset-edit-control {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  min-height: 42px;
}
.asset-edit-control:disabled {
  background: #f1f5f9 !important;
  color: #334155 !important;
}
.asset-date-control {
  color-scheme: light;
  accent-color: #8b1634;
}
.asset-date-control::-webkit-calendar-picker-indicator {
  filter: sepia(1) saturate(5) hue-rotate(285deg);
  opacity: 0.9;
  cursor: pointer;
}
.asset-theme-calendar-popper {
  z-index: 1600 !important;
}
.asset-theme-calendar {
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  overflow: hidden;
  font-family: inherit;
}
.asset-theme-calendar .react-datepicker__header {
  background: linear-gradient(135deg, #8b1634 0%, #b42352 100%) !important;
  border-bottom: none !important;
}
.asset-theme-calendar .react-datepicker__current-month,
.asset-theme-calendar .react-datepicker__day-name {
  color: #fff !important;
}
.asset-theme-calendar .react-datepicker__day {
  border-radius: 8px !important;
}
.asset-theme-calendar .react-datepicker__day:hover {
  background: #fce7f3 !important;
  color: #8b1634 !important;
}
.asset-theme-calendar .react-datepicker__day--selected,
.asset-theme-calendar .react-datepicker__day--keyboard-selected {
  background: #8b1634 !important;
  color: #fff !important;
}
.asset-theme-calendar .react-datepicker__navigation-icon::before {
  border-color: #fff !important;
}
`;
if (typeof window !== "undefined" && !document.getElementById("asset-edit-modal-style")) {
  style.id = "asset-edit-modal-style";
  document.head.appendChild(style);
}
